import PageWrapper from "../components/PageWrapper";
import DataCardTable from "../components/DataCardTable";
import {propertyService} from "../../services/propertyService";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import moment from "moment";
import {Link} from "react-router-dom";
import RatingsOverlay from "../ratings/ratings-overlay";

const contractTypeLabels = {
    traditional: "Traditional renting",
    renting: "Hosting Services",
    undefined: "Undefined"
};

const adminFeeLabels = {
    "no": "None",
    "1/2month": "1/2 Month",
    "1month": "1 Month",
    "3/2months": "3/2 Months",
    "2months": "2 Months",
    "5/2months": "5/2 Months",
    "3months": "3 Months",
    "specific_value": "Specific"
};

const Listings = ({inline, cardTableStyles}) => {

    const {t} = useTranslation();

    const handlePreview = (listing) => () => {
        propertyService.previewListing(listing);
    }

    const getRentInterval = (row) => {
        if (row.minRent == null && row.maxRent == null) return "-";
        if (row.minRent === row.maxRent || row.maxRent == null) return row.minRent + "€";
        if (row.minRent == null) return row.maxRent + "€";
        return row.minRent + " - " + row.maxRent + "€";
    }

    const getAdminFee = (row) => {
        if (!row.extraAdministrativeFeeType || row.extraAdministrativeFeeType === "no") return "-";
        if (row.extraAdministrativeFeeType === "specific_value") return (row.extraAdministrativeFee || 0) + "€";
        return adminFeeLabels[row.extraAdministrativeFeeType] || row.extraAdministrativeFeeType;
    }

    const dataColumns = [
        {
            name: 'ID',
            width: "100px",
            selector: row => row.id,
            cell: row => <OverlayTrigger overlay={<Tooltip>{row.listing_id || row.id}</Tooltip>}>
                <div data-tag="allowRowEvents">{"#" + ((((row.listing_id || row.id).split("_")[1])||((row.listing_id || row.id).split("_")[0])).split("-")[0])}</div>
            </OverlayTrigger>
        },
        {
            name: 'Address',
            minWidth: "200px",
            maxWidth: "260px",
            selector: row => row.address,
            cell: row => (<div data-tag="allowRowEvents" className={"property-cell"}>
                <span>{row.address}</span>
            </div>)
        },
        {
            name: 'City',
            minWidth: "90px",
            maxWidth: "130px",
            selector: row => row.region
        },
        {
            name: 'Neighborhood',
            minWidth: "120px",
            maxWidth: "180px",
            selector: row => row.neighborhood,
            cell: row => <div data-tag="allowRowEvents">{row.neighborhood || "-"}</div>
        },
        {
            name: 'Landlord',
            minWidth: "150px",
            maxWidth: "220px",
            selector: row => row.landlord_name,
            cell: row => row.landlord_id ?
                <Link to={`/landlords/${row.landlord_id}`}>{row.landlord_name || row.landlord_id}</Link> :
                <div>-</div>
        },
        {
            name: 'Room',
            minWidth: "120px",
            maxWidth: "180px",
            selector: row => row.internalName,
            cell: row => <div data-tag="allowRowEvents">{row.internalName || "To be defined"}</div>
        },
        {
            name: 'Accommodation type',
            selector: row => row.accommodation,
            cell: row => <div
                data-tag="allowRowEvents">{row.accommodation ? t(`property_resume.accommodation_type.${row.accommodation}`) : " - "}</div>
        },
        {
            name: 'Rent',
            selector: row => row.minRent,
            cell: row => (<OverlayTrigger
                overlay={<Tooltip>{row.billsIncluded ? 'Expenses included' : 'Expenses not included'}</Tooltip>}>
                <div data-tag="allowRowEvents">{getRentInterval(row)}</div>
            </OverlayTrigger>)
        },
        {
            name: 'Fixed rent',
            center: true,
            selector: row => row.fixedRent,
            cell: row => <div data-tag="allowRowEvents">{row.fixedRent ? "Yes" : "No"}</div>
        },
        {
            name: 'Available from',
            minWidth: "130px",
            selector: row => row.firstAvailability,
            cell: row => <div data-tag="allowRowEvents">{row.openResidenceCalendar ? "Calendar always open" :
                (row.firstAvailability ?
                    (moment(row.firstAvailability).isBefore(moment()) ? moment() : moment(row.firstAvailability)).format('D MMM yyyy') :
                    "To be defined")}</div>
        },
        {
            name: 'Contract',
            minWidth: "120px",
            selector: row => row.contractType,
            cell: row => <div
                data-tag="allowRowEvents">{contractTypeLabels[row.contractType] || contractTypeLabels["undefined"]}</div>
        },
        {
            name: 'Admin fee',
            center: true,
            selector: row => row.extraAdministrativeFeeType,
            cell: row => <div data-tag="allowRowEvents">{getAdminFee(row)}</div>
        },
        {
            name: 'Instant Booking',
            center: true,
            selector: row => row.instantBooking,
            cell: row => <div data-tag="allowRowEvents">{row.instantBooking ? "Yes" : "No"}</div>
        },
        {
            name: 'Actions',
            width: "100px",
            button: true,
            cell: row => <div className={"actions-container"}>
                <RatingsOverlay ratings={row.ratings}/>
                <OverlayTrigger overlay={<Tooltip>Edit</Tooltip>}>
                    <Link to={`/property/${row.id}`}><i className={"mdi mdi-pencil-box-outline preview-action"}/></Link>
                </OverlayTrigger>
                <OverlayTrigger overlay={<Tooltip>Preview</Tooltip>}>
                    <i className={"mdi mdi-eye preview-action"} onClick={handlePreview(row)}/>
                </OverlayTrigger>
            </div>
        }
    ]

    const [filters, setFilters] = useState(JSON.parse(localStorage.getItem("listings_filters")) || {});

    return (
        <PageWrapper breadcrumbs={[{title: "Listings"}]}
                     title={"Listings"}
                     inline={inline} filters={filters} setFilters={setFilters}>
            <DataCardTable dataType={"listings"}
                           customClass={cardTableStyles}
                           fetchData={propertyService.listListings}
                           columns={dataColumns}
                           filters={filters}
            />
        </PageWrapper>
    )
};

export default Listings;
